import { firestore } from '@src/api/Firebase/Firebase';
import { collection } from 'firebase/firestore';
export enum CashFlowType {
    Input = 'Input',
    Output = 'Output',
    Shipping = 'Shipping'
}
/**
 * Renglón del Estado de Resultados al que pertenece una categoría de gasto
 * (PLAN-GASTOS-Y-ADMIN-DE-SUCURSAL §3.3). Nombres y orden: `EXPENSE_GROUPS`.
 * Variable = servicios de monto distinto cada mes (luz, agua, gas); operativo = lo
 * del día (basura, mandados). Decisión del negocio, §8.1.
 */
export enum ExpenseGroup {
    Supplies = 'supplies',
    Payroll = 'payroll',
    Fixed = 'fixed',
    Maintenance = 'maintenance',
    Operating = 'operating',
    Variable = 'variable',
    /** Sale de la caja pero NO es gasto del negocio: propinas entregadas, ajustes de caja. */
    OutsideStatement = 'outsideStatement'
}

export type PurchaseUnitMode = 'presentation' | 'base';

/** Lo que `syncPurchase` del server escribe en el gasto (paridad con `InventoryPurchase.ts` del server). */
export interface PurchaseInventoryState {
    synced: boolean;
    /** `null` cuando el gasto dejó de ser de insumo y lo que había entrado ya salió. */
    inputId?: string | null;
    quantity: number;
    unitMode: PurchaseUnitMode;
    /** Unidades base que el ledger tiene por este gasto (el factor del catálogo, congelado). */
    baseUnits: number;
    movementIds: string[];
    at: any;
}

export interface CashFlow {
    id?: string;
    userId?: string;
    branchOfficeId?: string;
    type: CashFlowType;
    amount: number;
    description?: string;
    orderId?: string; // Just for orders
    /** El instante en que se guardó (`stampNewCashFlow`). Los anteriores al 2026-09-27, a medianoche. */
    date: any;

    // ---- Gastos (plan de Gastos §3.1). Todo opcional: lo histórico se lee igual. ----
    /** Ausente = histórico "Sin categoría" (o "Envío" si `type` es Shipping). */
    categoryId?: string;
    /** Copias: renombrar una categoría no reescribe la historia. */
    categoryName?: string;
    categoryGroup?: ExpenseGroup;
    /** ¿Salió del efectivo de la caja? Ausente = sí (todo lo histórico). Solo lo que sí, resta en el corte. */
    paidFromCash?: boolean;
    payrollUserId?: string;
    payrollUserName?: string;
    /** Compra de insumo: el insumo elegido de la lista (`inputs`), con copia del nombre. */
    inputId?: string;
    inputName?: string;
    // ---- Compra en la calle (plan de inventario F1): lo que entra al inventario. ----
    /** Cuánto se compró, en `purchaseUnitMode`. Obligatorio desde F1 en "Compra de insumo". */
    purchaseQuantity?: number;
    /** Presentaciones del catálogo (default: compran igual que al CEDIS) o unidad base. */
    purchaseUnitMode?: PurchaseUnitMode;
    /** Copia de la unidad con la que se capturó ("pza", "rebanadas"): el texto no depende del catálogo de hoy. */
    purchaseUnitLabel?: string;
    /**
     * `true` = el ledger todavía no refleja este gasto. Lo pone el admin en CADA escritura
     * que toca la compra (crear, editar, cancelar) y lo borra el server al igualar el
     * ledger (`POST /inventory/purchases/:id/sync`); si el server no contestó, el aseo
     * diario lo reintenta. Consulta de un campo.
     */
    inventorySyncPending?: boolean;
    /** Lo que el server dejó al igualar el ledger: cantidad, modo y factor congelados, movimientos. */
    inventory?: PurchaseInventoryState;
    /** SOLO en memoria (nunca se escribe): por qué no entró al inventario, para decírselo a quien capturó. */
    inventorySyncError?: string;
    /** Lo que escribió quien capturó, sin el encabezado que agrega la categoría (nómina, insumo). */
    note?: string;
    recurringExpenseId?: string;
    /** `yyyy-MM`: de qué mes es un gasto fijo o variable (la luz de septiembre se paga en octubre). */
    period?: string;
    /**
     * Instancia de un gasto fijo que generó el aseo y nadie ha confirmado: el Estado de
     * Resultados la cuenta como "estimado" y **no resta en el corte** hasta confirmarse.
     */
    pendingConfirmation?: boolean;
    /** La plantilla dice que sale de la caja: al confirmarla, resta en la caja abierta. */
    paidFromCashOnConfirm?: boolean;
    /** `'cron-daily'` cuando lo generó el aseo (instancia de un gasto fijo). */
    generatedBy?: string;
    confirmedAt?: any;
    confirmedBy?: string;
    confirmedByName?: string;
    editedAt?: any;
    editedBy?: string;
    editedByName?: string;
    /** Ausente = activo. Un gasto capturado NUNCA se borra: se cancela y queda a la vista. */
    status?: 'deleted';
    deletedAt?: any;
    deletedBy?: string;
    deletedByName?: string;
    deleteReason?: string;
}
/** Lo que un escritor entrega: quién, dónde, cuándo y el id los pone `saveCashFlow`. */
export type NewCashFlow = Omit<CashFlow, 'id' | 'userId' | 'branchOfficeId' | 'date'>;

/**
 * Arma el documento de un movimiento nuevo. **La fecha es el instante en que se
 * guarda**, nunca la medianoche.
 *
 * 🚨 Hasta el 2026-09-27 los dos escritores la fijaban a las 00:00 (la
 * calculadora, además, una sola vez al cargar el módulo: una tablet abierta
 * pasada la medianoche escribía AYER). El corte reparte cada movimiento a la caja
 * cuya ventana lo contiene —de su apertura real a su cierre—, así que un gasto de
 * las 00:00 no caía en ninguna: **el esperado nunca restaba los gastos** y el
 * corte ciego acusaba de faltante justo lo que se había gastado.
 *
 * `undefined` no es un valor de Firestore: la sucursal solo va si existe.
 */
export const stampNewCashFlow = (
    cashFlow: NewCashFlow,
    stamp: { id: string; userId: string; branchOfficeId?: string; now: Date }
): CashFlow => ({
    ...cashFlow,
    id: stamp.id,
    userId: stamp.userId,
    ...(stamp.branchOfficeId ? { branchOfficeId: stamp.branchOfficeId } : {}),
    date: stamp.now
});

export const CASH_FLOWS_COLLECTION = 'cash-flows';
export const cashFlowsRef = collection(firestore, CASH_FLOWS_COLLECTION);
